<script setup lang="ts">
import {computed, reactive, ref, watch} from "vue";
import dayjs from "dayjs";
import {Dialog, Button, Label} from "@/Components/Library";
import DatePicker from "primevue/datepicker";
import {updatePublishDates, updateMultipleGroupQuestions} from "@/Services/group-questions";
import {useToast} from "primevue/usetoast";

const toast = useToast();

const props = defineProps<{
    groupId: string | number;
    question?: any;
    groupQuestionIds?: string[];
    minPublishTo?: string | null;
}>();
const emit = defineEmits<{
    saved: [{publish_to: string}];
}>();
const visible = defineModel<boolean>("visible", {required: true});

const isBulkMode = computed(() => props.groupQuestionIds && props.groupQuestionIds.length > 0);

const publishDates = reactive({
    publish_from: null as Date | null,
    publish_to: null as Date | null,
});

const loading = ref(false);

const bulkMinDate = computed(() => {
    if (props.minPublishTo) {
        return dayjs(props.minPublishTo).toDate();
    }
    return new Date();
});

const savePublishDates = async () => {
    if (isBulkMode.value) {
        await saveBulkPublishDates();
    } else {
        await saveSinglePublishDates();
    }
};

const saveSinglePublishDates = async () => {
    if (!props.question) {
        return;
    }

    const dates = {
        publish_from: dayjs(publishDates.publish_from).format("YYYY-MM-DD HH:mm:ss"),
        publish_to: dayjs(publishDates.publish_to).format("YYYY-MM-DD HH:mm:ss"),
    };

    try {
        loading.value = true;
        await updatePublishDates(props.groupId, props.question.group_question_id, dates);
        props.question.publish_from = dates.publish_from;
        props.question.publish_to = dates.publish_to;
        visible.value = false;
        toast.add({
            severity: "success",
            summary: "Publish dates updated",
            detail: "The publish dates have been updated.",
            life: 3000,
        });
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const saveBulkPublishDates = async () => {
    if (!props.groupQuestionIds?.length) {
        return;
    }

    const publishTo = dayjs(publishDates.publish_to).format("YYYY-MM-DD HH:mm:ss");

    try {
        loading.value = true;
        await updateMultipleGroupQuestions(props.groupId, {
            group_questions: props.groupQuestionIds,
            publish_to: publishTo,
        });
        visible.value = false;
        emit("saved", {publish_to: publishTo});
    } catch (error) {
        console.error(error);
    } finally {
        loading.value = false;
    }
};

watch(
    () => props.question,
    (newQuestion) => {
        if (newQuestion && !isBulkMode.value) {
            publishDates.publish_from = newQuestion.publish_from ? dayjs(newQuestion.publish_from).toDate() : null;
            publishDates.publish_to = newQuestion.publish_to ? dayjs(newQuestion.publish_to).toDate() : null;
        }
    },
    {immediate: true},
);

watch(
    () => visible.value,
    (newVisible) => {
        if (newVisible && isBulkMode.value) {
            publishDates.publish_to = null;
        }
    },
);
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        dismissable-mask
        class="w-full max-w-lg overflow-hidden"
        :header="isBulkMode ? `Extend deadline for ${groupQuestionIds?.length} posts` : 'Update publish dates'"
    >
        <form @submit.prevent="savePublishDates">
            <div>
                <div v-if="!isBulkMode" class="mb-4 flex flex-col">
                    <Label for="publish_from" class="mb-1" required>Publish from</Label>
                    <DatePicker
                        id="publish_from"
                        name="publish_from"
                        placeholder="Select a date to publish the question from"
                        fluid
                        showTime
                        hourFormat="24"
                        :min-date="new Date()"
                        v-model="publishDates.publish_from"
                        :hideOnDateTimeSelect="true"
                        pt:pcinputtext:root:required
                        dateFormat="dd/mm/yy"
                    />
                </div>
                <div class="mb--4 flex flex-col">
                    <Label for="publish_to" class="mb-1" required>Publish until</Label>
                    <DatePicker
                        id="publish_to"
                        name="publish_to"
                        placeholder="Select a date to publish the question until"
                        fluid
                        showTime
                        hourFormat="24"
                        :min-date="isBulkMode ? bulkMinDate : (publishDates.publish_from ?? new Date())"
                        v-model="publishDates.publish_to"
                        :hideOnDateTimeSelect="true"
                        pt:pcinputtext:root:required
                        dateFormat="dd/mm/yy"
                    />
                </div>
                <Button
                    type="submit"
                    class="mt-4 w-full bg-askr-green text-white hover:brightness-110"
                    :loading="loading"
                >
                    Save
                </Button>
            </div>
        </form>
    </Dialog>
</template>
